import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  viewChild,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { TranslatePipe } from '@ngx-translate/core';
import { IconTemplateRef } from '../../pages/shipments/shipments-configurations/shipment-instructions/components/action-slot/action-slot.types';
import { Omit } from '../../store/contacts/contacts.types';
import { ContextMenuItem } from './context-menu.types';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatIconButton, MatMenuModule, MatIconModule, MatDivider, TranslatePipe],
  selector: 'orion-platform-context-menu',
  standalone: true,
  styleUrls: ['./context-menu.component.scss'],
  templateUrl: './context-menu.component.html',
})
export class ContextMenuComponent<TRights = void> {
  /**
   * Custom style configuration passed from the parent component (to style the menu trigger button).
   */
  readonly style = input<Omit<IconTemplateRef, '$implicit'>>();

  /**
   * List of menu items shown in the context menu.
   * Each item can specify rights, label, disabled state, icon, divider, etc.
   */
  readonly items = input<Array<ContextMenuItem<TRights>>>([]);

  /**
   * Menu size variant:
   * - `normal` — full Material spacing (default)
   * - `small` — compact spacing & reduced font size
   *
   * Useful when the menu is part of a dense table or compact UI area.
   */
  readonly type = input<'normal' | 'small'>('small');

  /**
   * When `true`, the default `mat-icon-button` (more_vert) trigger is NOT rendered.
   * Consumers must provide their own trigger and bind it via the exposed `menu`
   * property, e.g.:
   * ```html
   * <orion-platform-context-menu #ctx [hideTrigger]="true" [items]="..." />
   * <button mat-fab [matMenuTriggerFor]="ctx.menu()">...</button>
   * ```
   */
  readonly hideTrigger = input<boolean>(false);

  /**
   * Material icon name used on the default trigger button. Defaults to
   * `more_vert` (standard context-menu glyph). Set to `settings`, `tune`,
   * etc. to keep semantic icons when refactoring older menus.
   */
  readonly triggerIcon = input<string>('more_vert');

  /**
   * Pre-registered SVG icon name (via `MatIconRegistry.addSvgIcon`) for the
   * default trigger button. When set, the BB renders
   * `<mat-icon [svgIcon]="..." />` and ignores `triggerIcon`. Use for custom
   * brand icons that aren't available in Material Icons font (e.g. the
   * dashboard widgets' `orion-settings` glyph registered in
   * `BaseDashboardPanelComponent`).
   */
  readonly triggerSvgIcon = input<string>();

  /**
   * Disables the default trigger button (greyed-out, non-interactive) but
   * keeps it visible — restores the look the dashboard widgets had with
   * `<button [disabled]="!visibility().onDashboard">` before the refactor.
   * Has no effect when `hideTrigger=true` (consumer renders their own button).
   */
  readonly triggerDisabled = input<boolean>(false);

  /**
   * How `ContextMenuItem.selected` is rendered:
   * - `'check'` (default) — selected items get a `check` glyph, unselected
   *   render whatever `icon` they have (or nothing).
   * - `'radio'` — every item gets a radio glyph (`radio_button_checked` for
   *   selected, `radio_button_unchecked` otherwise) regardless of `icon`.
   *   Use this for single-select menus that replace `mat-radio-group`
   *   markup (e.g. the toolbar plant / language pickers).
   */
  readonly selectionMode = input<'check' | 'radio'>('check');

  /**
   * Horizontal opening direction of the mat-menu panel relative to the
   * trigger button:
   *   - `'after'` (default, matches Material) — panel opens to the right
   *     of the trigger's leading edge; used for menus rendered mid-page or
   *     on the left side of the viewport.
   *   - `'before'` — panel opens to the LEFT of the trigger's trailing
   *     edge; the correct choice for triggers pinned to the right edge of
   *     the viewport (toolbar plant / language / user pickers), where the
   *     default 'after' would push the panel off-screen and Material 21's
   *     auto-flip lands it in the top-left corner instead of hugging the
   *     trigger.
   * Material still flips automatically when the chosen side has no room,
   * so setting this only tightens the DEFAULT — it isn't a hard lock.
   */
  readonly xPosition = input<'after' | 'before'>('after');

  /**
   * Vertical opening direction — same contract as `xPosition` but on the
   * y axis. Exposed for symmetry (some toolbars sit at the bottom of the
   * viewport and want `'above'`); left at Material's `'below'` default.
   */
  readonly yPosition = input<'above' | 'below'>('below');

  /**
   * The internal `MatMenu` instance — exposed so a consumer with `hideTrigger=true`
   * can wire its own trigger via `[matMenuTriggerFor]="ctx.menu()"` —
   * a signal query, so the call parentheses are required.
   */
  readonly menu = viewChild.required<MatMenu>('menu');

  /**
   * Base identifier for this menu.
   *
   * The panel gets it as-is, the trigger gets `<testId>-trigger`, and every row
   * gets `<testId>-item-<name>` — so a consumer names the menu once instead of
   * labelling each row it builds, and the panel and the button that opens it
   * are told apart rather than sharing one name.
   */
  readonly testId = input<string>();

  /**
   * The id for the button that opens the menu.
   *
   * Distinct from the panel's, because both are in the document at once while
   * the menu is open and a name that matches two elements picks neither.
   */
  readonly triggerTestId = computed((): null | string => {
    const base = this.testId();

    return base ? `${base}-trigger` : null;
  });

  /**
   * The id for one row: its own if it named one, otherwise derived from the
   * menu's. Nothing at all when the menu was not given a `testId` — an empty
   * `data-testid` is worse than none, because it matches every other one.
   * @param {ContextMenuItem} item The row.
   * @returns {string | null} What to render as `data-testid`.
   */
  itemTestId(item: ContextMenuItem<TRights>): null | string {
    if (item.testId) return item.testId;

    const base = this.testId();

    return base ? `${base}-item-${item.name}` : null;
  }

  /**
   * Emits the `name` property of the selected menu action.
   * Consumers listen to `(actionSelected)` for contextual actions.
   */
  readonly actionSelected = output<string>();

  /**
   * Handles click behavior for a menu item.
   * Disabled items and dividers cannot emit actions.
   *
   * @param item The menu item the user clicked on.
   */
  onItemClick(item: ContextMenuItem<TRights>): void {
    if (item.disabled || item.divider) {
      return;
    }

    this.actionSelected.emit(item.name);
  }
}
